// src/components/RouteGuard.tsx (Updated)
import React from 'react'
import { Navigate, useLocation } from 'react-router-dom'
import { useAuthStore } from '../stores/authStore'
import { useUserClaims } from '../hooks/useUserClaims'

interface RouteGuardProps {
  children: React.ReactNode
  requiredRole?: 'super_admin' | 'admin' | 'sales_user' | 'market_access_user'
  requiredRoles?: Array<
    'super_admin' | 'admin' | 'sales_user' | 'market_access_user'
  >
  requireAuth?: boolean
}

/**
 * Route guard component that protects routes based on authentication and roles
 */
export function RouteGuard({
  children,
  requiredRole,
  requiredRoles,
  requireAuth = true,
}: RouteGuardProps) {
  const location = useLocation()
  const { isAuthenticated, isLoading, isInitialized } = useAuthStore()
  const { claims } = useUserClaims()

  // Show loading spinner only if auth is not yet initialized
  if (!isInitialized || (isLoading && !isAuthenticated)) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading...</p>
        </div>
      </div>
    )
  }

  // Check if authentication is required
  if (requireAuth && !isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />
  }

  // Check role requirements
  if ((requiredRole || requiredRoles) && claims) {
    const hasRequiredRole = requiredRole
      ? claims.role === requiredRole
      : requiredRoles?.includes(claims.role)

    if (!hasRequiredRole) {
      return <Navigate to="/unauthorized" replace />
    }
  }

  return <>{children}</>
}

// Convenience components for common role checks
export function AdminRoute({ children }: { children: React.ReactNode }) {
  return (
    <RouteGuard requiredRoles={['admin', 'super_admin']}>{children}</RouteGuard>
  )
}

export function SuperAdminRoute({ children }: { children: React.ReactNode }) {
  return <RouteGuard requiredRole="super_admin">{children}</RouteGuard>
}
